<template>
  <div
    :class="isMobile ? 'tab-content-card--mobile' : 'tab-content-card'"
  >
    <v-card-title-wrapper
      v-if="config.title"
      class="v-card-title"
    >
      {{ config.title }}
    </v-card-title-wrapper>

    <div
      :class="isMobile ? 'v-card-text--mobile' : 'v-card-text'"
    >
      <v-row-wrapper
        v-for="(member, index) in modifiedFormData[dataKey]"
        :key="index"
        align="end"
        no-gutters
        class="crew-member-row"
      >
        <!-- Not the full row width - from md up the field takes one column of
             the 4-per-row grid the rest of this screen uses, with the delete
             button right next to it. Below md it keeps filling the row, where
             there's no width to give away. -->
        <v-col-wrapper
          md="4"
          @dblclick="unlockFieldsOnDblClick"
        >
          <autocomplete-field
            v-model="member.person_id"
            :field="memberField(index)"
            :options="autocompleteOptions"
            :validation-errors="validationErrors"
            :update-data="updateData"
            :update-validation-errors="updateValidationErrors"
            :collection-key="dataKey"
            :collection-index="index"
            :custom-field-key="'member_ids.' + index"
            :disabled="!canEdit"
            :edit-mode="edit"
            :create-mode="createMode"
            @clear-validation-errors="$emit('clear-validation-errors', 'member_ids.' + index)"
          />
        </v-col-wrapper>

        <v-col-wrapper
          v-if="canEdit"
          cols="auto"
          class="d-flex crew-member-row__delete"
        >
          <universal-button
            type="delete-action"
            text
            class="no-background-hover"
            :ripple="false"
            @click="removeMember(index)"
          />
        </v-col-wrapper>
      </v-row-wrapper>

      <universal-button
        v-if="canEdit"
        type="link"
        text
        color="#65AD45"
        prepend-icon="mdi-plus"
        :prepend-icon-style="{ fontSize: '16px', marginRight: '4px', color: '#65AD45' }"
        class="crew-add-button"
        :label="translate('base.add_new_row')"
        @click="addMember"
      />
    </div>
  </div>
</template>

<script setup>
import Vue, { computed, watch } from 'vue'
import AutocompleteField from '@/global/components/fields/AutocompleteField.vue'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import VRowWrapper from '@/global/components/vuetifyComponentsWrappers/VRowWrapper.vue'
import VColWrapper from '@/global/components/vuetifyComponentsWrappers/VColWrapper.vue'
import VCardTitleWrapper from '@/global/components/vuetifyComponentsWrappers/VCardTitleWrapper.vue'
import { useTranslate } from '@/global/composables/useTranslate'
import { useAppContext } from '@/global/services/compositionApi/useAppContext'

const DEFAULT_DATA_KEY = 'crew_members'

const props = defineProps({
  config: {
    type: Object,
    default: () => ({})
  },
  formData: {
    type: Object,
    default: () => ({})
  },
  edit: {
    type: Boolean,
    default: false
  },
  createMode: {
    type: Boolean,
    default: false
  },
  permissions: {
    type: Object,
    default: null
  },
  validationErrors: {
    type: Object,
    default: () => ({})
  },
  updateData: {
    type: Function,
    default: () => {}
  },
  updateValidationErrors: {
    type: Function,
    default: () => {}
  }
})

const emit = defineEmits([
  'clear-validation-errors',
  'set-edit'
])

const { translate } = useTranslate()
const { vuetify } = useAppContext()

const modifiedFormData = computed(() => props.formData)

const dataKey = computed(() => props.config?.dataKey || DEFAULT_DATA_KEY)

const canEdit = computed(() => (props.edit || props.createMode) && !(props.permissions && !props.permissions.canEdit))

const isMobile = computed(() => !!vuetify?.breakpoint?.smAndDown)

const autocompleteOptions = computed(() => props.config?.autocomplete_options || {})

const memberField = (index) => ({
  key: 'person_id',
  label: translate('road-maintenance/temporary_regulation/crew_details.employee') + ' ' + (index + 1),
  editable: true
})

const syncCrewMembers = () => {
  if (!modifiedFormData.value) {
    return
  }

  const existingMembers = Array.isArray(modifiedFormData.value[dataKey.value]) ? modifiedFormData.value[dataKey.value] : []

  Vue.set(modifiedFormData.value, dataKey.value, existingMembers.length ? existingMembers : [{ person_id: null }])
}

watch(() => props.formData, syncCrewMembers, { immediate: true })

const addMember = () => {
  modifiedFormData.value[dataKey.value].push({ person_id: null })
}

const removeMember = (index) => {
  modifiedFormData.value[dataKey.value].splice(index, 1)
}

// Double-clicking a member field while viewing (not yet editing) turns on
// edit mode for the whole tab, same as the generic dynamic-columns/
// fields-renderer blocks elsewhere in the app.
const unlockFieldsOnDblClick = () => {
  if (!props.createMode && !(props.permissions && !props.permissions.canEdit)) {
    emit('set-edit', true)
  }
}
</script>

<style scoped lang="scss">
.tab-content-card > *:last-child {
  border-bottom-right-radius: 16px !important;
  border-bottom-left-radius: 16px !important;
  margin-bottom: 10px;
}

.tab-content-card--mobile {
  width: 100%;
  margin: 0 !important;
}

.v-card-title {
  font-family: 'Satoshi', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #111827;
  margin-top: 1%;
}

.v-card-text {
  padding: 16px;
  margin: 0 15px;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
}

.v-card-text--mobile {
  padding: 16px;
  margin-left: 1%;
  margin-right: 1%;
}

.crew-member-row {
  margin-bottom: 8px;
}

.crew-member-row__delete {
  /* Centered on the input box itself. The column is pinned to the top of the
     row and given exactly the input's own geometry - offset down by the label
     above it, then as tall as the input - so the icon's centering never depends
     on the row's height. That's what keeps it centered once a validation
     message appears under the field and makes the row taller. */
  --crew-member-label-height: 24px; /* 20px label line + the field's own 4px `mt-1` */
  --crew-member-field-height: 40px; /* dense outlined input */

  padding-left: 8px;
  align-self: flex-start;
  align-items: center;
  margin-top: var(--crew-member-label-height);
  height: var(--crew-member-field-height);
}

.crew-add-button {
  margin-top: 4px;
}

::v-deep .crew-add-button {
  text-transform: none;
  height: auto !important;
  min-width: unset !important;
  padding: 0 !important;
}

::v-deep .crew-add-button .button-label {
  font-weight: 500;
}
</style>
